/**
 * Flow System User Constants
 *
 * This module provides constants and utilities for working with Flow system users:
 * - Flow AI Assistant (automated AI responses)
 *
 * Note: Support conversations are now identified by metadata.is_support = true
 * and include all super admins as participants (no dedicated support user).
 */

// =============================================================================
// BALLEE AI ASSISTANT
// =============================================================================

/**
 * Well-known UUID for the Flow AI Assistant system user.
 * This UUID is deterministic and consistent across all environments.
 */
export const BALLEE_USER_ID = '00000000-0000-0000-0000-000000000001';

/**
 * Display name for Flow shown in conversations
 */
export const BALLEE_DISPLAY_NAME = 'Flow AI Assistant';

/**
 * Short name for Flow (used in compact UI)
 */
export const BALLEE_SHORT_NAME = 'Flow';

/**
 * Avatar URL for Flow
 */
export const BALLEE_AVATAR_URL = '/images/flow-avatar.svg';

/**
 * Check if a user ID is the Flow AI assistant
 */
export function isBalleeUser(userId: string | null | undefined): boolean {
  return userId === BALLEE_USER_ID;
}

/**
 * Check if a conversation includes Flow as a participant
 */
export function isBalleeConversation(
  participants: Array<{ user_id: string }> | null | undefined,
): boolean {
  if (!participants) return false;
  return participants.some((p) => isBalleeUser(p.user_id));
}

/**
 * Get the Flow user ID (useful for creating conversations)
 */
export function getBalleeUserId(): string {
  return BALLEE_USER_ID;
}

// =============================================================================
// SUPPORT CONVERSATIONS
// =============================================================================

/**
 * Check if a conversation is a support conversation by metadata flag.
 * Support conversations are group chats with all super admins as participants.
 */
export function isSupportConversationByMetadata(
  metadata: Record<string, unknown> | null | undefined,
): boolean {
  return metadata?.is_support === true;
}

// =============================================================================
// SHARED UTILITIES
// =============================================================================

/**
 * Check if a user ID is a Flow system user (AI assistant)
 */
export function isSystemUser(userId: string | null | undefined): boolean {
  return isBalleeUser(userId);
}

// =============================================================================
// BALLEE AI PAGE CONTEXT
// =============================================================================

/**
 * Page context passed to Flow for contextual responses
 */
export interface BalleePageContext {
  /** Type of entity being viewed (event, production, dancer, client, venue) */
  entityType?: 'event' | 'production' | 'dancer' | 'client' | 'venue';
  /** ID of the entity being viewed */
  entityId?: string;
  /** Human-readable name of the entity */
  entityName?: string;
  /** Current page path */
  pagePath?: string;
  /** Type of page (list, detail, edit, etc.) */
  pageType?: 'list' | 'detail' | 'edit' | 'create' | 'dashboard';
}

/**
 * Serialize page context for URL params
 */
export function serializePageContext(context: BalleePageContext): string {
  return encodeURIComponent(JSON.stringify(context));
}

/**
 * Deserialize page context from URL params
 */
export function deserializePageContext(
  serialized: string | null | undefined,
): BalleePageContext | null {
  if (!serialized) return null;
  try {
    return JSON.parse(decodeURIComponent(serialized)) as BalleePageContext;
  } catch {
    return null;
  }
}
